/**
 * Sugestão inteligente de TPL — monta agenda inicial da Biblioteca Lean
 */

import { getLibraryForUser } from './leanLibraryService';
import { leanItemToTemplatePayload, leanNivelToLstCargo } from '../types/leanLibrary';
import type { LeanLibraryItem } from '../types/leanLibrary';
import type { LstCargo, LstTemplate } from '../types';
import type { LstTemplateInput } from './lstService';

export interface TplAiSuggestContext {
  lstCargo: LstCargo;
  roleName?: string | null;
  subordinateCount: number;
  operationComplexity: 'baixa' | 'media' | 'alta';
  existingTitles: string[];
}

export interface TplSuggestedSlot {
  item: LeanLibraryItem;
  suggestedTime: string;
  suggestedDay: 1 | 2 | 3 | 4 | 5;
  priority: number;
}

export interface TplAiSuggestResult {
  slots: TplSuggestedSlot[];
  summary: string;
  totalWeeklyMinutes: number;
  templatesToCreate: Partial<LstTemplateInput>[];
}

const DEFAULT_TIMES = ['07:00', '08:00', '09:30', '11:00', '14:00', '15:30', '16:30'];

function complexityMultiplier(complexity: TplAiSuggestContext['operationComplexity']): number {
  if (complexity === 'alta') return 1.25;
  if (complexity === 'media') return 1;
  return 0.85;
}

function scoreItem(item: LeanLibraryItem, ctx: TplAiSuggestContext): number {
  let score = 50;
  if (item.frequenciaRecomendada === 'Diária') score += 30;
  if (item.frequenciaRecomendada === 'Semanal') score += 20;
  if (item.categoria === 'Segurança') score += 15;
  if (item.categoria === 'Entrega') score += 10;
  if (/reunião|camada|gemba/i.test(item.titulo)) score += 12;
  if (ctx.subordinateCount > 5 && /coaching|one-on-one|auditoria tpl/i.test(item.titulo)) score += 15;
  if (ctx.subordinateCount === 0 && /lider|equipe|turno/i.test(item.titulo)) score -= 20;
  score *= complexityMultiplier(ctx.operationComplexity);
  return score;
}

function pickDayForFrequency(
  freq: LeanLibraryItem['frequenciaRecomendada'],
  index: number,
): 1 | 2 | 3 | 4 | 5 {
  if (freq === 'Diária') return ((index % 5) + 1) as 1 | 2 | 3 | 4 | 5;
  if (freq === 'Semanal') return ([1, 3, 5, 2, 4][index % 5] ?? 1) as 1 | 2 | 3 | 4 | 5;
  return 1;
}

export function suggestTplAgenda(ctx: TplAiSuggestContext): TplAiSuggestResult {
  const library = getLibraryForUser(ctx.lstCargo, ctx.roleName).filter(
    (item) => !ctx.existingTitles.some((t) => t.toLowerCase() === item.titulo.toLowerCase()),
  );

  const ranked = [...library]
    .map((item) => ({ item, priority: scoreItem(item, ctx) }))
    .sort((a, b) => b.priority - a.priority);

  const dailyCap = ctx.operationComplexity === 'alta' ? 12 : ctx.operationComplexity === 'media' ? 9 : 6;
  const weeklyCap = ctx.operationComplexity === 'alta' ? 8 : 5;
  const monthlyCap = 3;

  const selected: LeanLibraryItem[] = [];
  let d = 0;
  let w = 0;
  let m = 0;

  for (const { item } of ranked) {
    if (item.frequenciaRecomendada === 'Diária' && d < dailyCap) {
      selected.push(item);
      d += 1;
    } else if (item.frequenciaRecomendada === 'Semanal' && w < weeklyCap) {
      selected.push(item);
      w += 1;
    } else if (item.frequenciaRecomendada === 'Mensal' && m < monthlyCap) {
      selected.push(item);
      m += 1;
    }
    if (selected.length >= dailyCap + weeklyCap + monthlyCap) break;
  }

  const slots: TplSuggestedSlot[] = selected.map((item, i) => ({
    item,
    suggestedTime: DEFAULT_TIMES[i % DEFAULT_TIMES.length],
    suggestedDay: pickDayForFrequency(item.frequenciaRecomendada, i),
    priority: scoreItem(item, ctx),
  }));

  const totalWeeklyMinutes = slots.reduce((sum, s) => sum + s.item.duracaoRecomendada, 0);

  const templatesToCreate: Partial<LstTemplateInput>[] = slots.map((s) => {
    const payload = leanItemToTemplatePayload(s.item);
    const primaryNivel = s.item.nivelHierarquico[0];
    const targetRole = primaryNivel ? leanNivelToLstCargo(primaryNivel) : ctx.lstCargo;
    return {
      title: payload.title,
      description: payload.description,
      frequency: payload.frequency,
      category: payload.category,
      duration_minutes: payload.duration_minutes,
      activity_kind: payload.activity_kind,
      swi_text: payload.swi_text,
      is_critical: payload.is_critical,
      scheduled_time: s.suggestedTime,
      target_role: targetRole,
    };
  });

  return {
    slots,
    summary: `Sugestão com ${slots.length} rotinas da Biblioteca Lean para ${ctx.roleName ?? ctx.lstCargo}, considerando ${ctx.subordinateCount} liderado(s) e operação de complexidade ${ctx.operationComplexity}. Carga semanal estimada: ${totalWeeklyMinutes} min.`,
    totalWeeklyMinutes,
    templatesToCreate,
  };
}

export function applySuggestedTemplates(
  existing: LstTemplate[],
  suggestions: Partial<LstTemplateInput>[],
): Partial<LstTemplateInput>[] {
  const titles = new Set(existing.map((t) => t.title.toLowerCase()));
  return suggestions.filter((s) => s.title && !titles.has(s.title.toLowerCase()));
}
